import {
    type PropsWithChildren,
    createContext,
    useContext,
    useMemo
} from "react";

import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

/**
 * Context value for the WAC Refresh Q4 FY26 feature gate + experiment.
 * The check is performed once at the provider level and memoized so all
 * descendant consumers receive the same stable boolean without re-running
 * the gate/experiment logic on every render.
 */
type WacRefreshContextValue = {
    isWacRefresh: boolean;
};

const WacRefreshContext = createContext<WacRefreshContextValue>({
    isWacRefresh: false
});

/**
 * Provides the `isWacRefresh` flag to the component tree.
 */
export const WacRefreshProvider = ({ children }: PropsWithChildren) => {
    const isWACRefreshGateEnabled = checkFeatureFlagGate(
        featureFlags.wacRefreshTeam26Gate
    );

    const value = useMemo<WacRefreshContextValue>(
        () => ({
            isWacRefresh: isWACRefreshGateEnabled
        }),
        [isWACRefreshGateEnabled]
    );

    return (
        <WacRefreshContext.Provider value={value}>
            {children}
        </WacRefreshContext.Provider>
    );
};

/**
 * Returns `true` when the WAC Refresh Q4 FY26 feature gate is enabled.
 *
 * Reads from context — defaults to `false` when used outside of a
 * `<WacRefreshProvider>` (e.g. in unit tests that render components in
 * isolation).
 */
export const useWacRefresh = (): boolean => {
    const { isWacRefresh } = useContext(WacRefreshContext);
    return isWacRefresh;
};
